import { store } from 'store';

class A {
  async getAll(): Promise<any> {}
}

class B {
  async get(): Promise<any> {}
}

class D {
  async get(
    range: string,
    startDate: string,
    endDate: string,
    shouldSendDate: boolean,
    initialRun: boolean,
    assetType: string,
    aggregation: string,
    shiftWiseAnalysis: boolean,
    shiftNumber: number,
    utilizationType: string
  ): Promise<any> {}
}

class E {
  async get(
    range: string,
    startDate: string,
    endDate: string,
    shouldSendDate: boolean,
    initialRun: boolean,
    assetType: string,
    aggregation: string
  ): Promise<any> {}

  async getLoaderDetails(
    range: string,
    startDate: string,
    endDate: string,
    shouldSendDate: boolean,
    initialRun: boolean,
    assetType: string,
    aggregation: string
  ): Promise<any> {}
}

interface IHandlerMap {
  handlers: Partial<{
    internalUserHandler: A;
    customerHandler: A;
    externalUserHandler: A & {
      currentCustomer: () => Promise<any>;
    };
    assetHandler: A & {
      getAssetsWithExcessiveIdling: () => Promise<any>;
      getAllWithFuelAnalyticsData: () => Promise<any>;
    };
    operatorHandler: A;
    hierarchyHandler: B;
    ruleHandler: A;
    notificationHandler: A & {
      getAllLatest: () => Promise<any>;
    };
    deviceHandler: A & {
      getDeviceLatestSensorDataAll: () => Promise<any>;
    };
    tabletHandler?: A;
    tagHandler: A;
    engineHoursHandler: A;
    weatherHandler: B;
    fuelPriceHandler: B;
    // geoHandler: B;
    fleetOverviewHandler: A;
    utilizationHandler: D;
    productionHandler: E;
    // fuelAnalyticsHandler: D;
    tirePressureHandler: B;
    tagThresholdHandler: B;
    dtcHandler: B;
    tirePatternHandler: B;
    targetHandler: {
      getAll: (customerId: string, year: number) => Promise<any>;
    };
    customerId?: string;
    modules?: {
      isPartsModule: boolean;
      isTiresModule: boolean;
      isMachinesModule: boolean;
    };
  }>;
}
const fullYear = new Date().getFullYear();

// Common promises - called for ALL users (including assets which are needed for both TPMS and Machine)
export function getCommonPromises(
  handlers: IHandlerMap['handlers'],
  customerId?: string
) {
  return [
    handlers.weatherHandler?.get(),
    handlers.assetHandler?.getAll(),
    handlers.operatorHandler?.getAll(),
    handlers.ruleHandler?.getAll(),
    handlers.deviceHandler?.getAll(),
  ].filter(Boolean);
}

// TPMS-specific promises - called only for TPMS users
export function getTpmsPromises(handlers: IHandlerMap['handlers']) {
  return [
    handlers.tirePressureHandler?.get(),
    handlers.tagThresholdHandler?.get(),
    handlers.tirePatternHandler?.get(),
  ].filter(Boolean);
}

// Machine-specific promises - called only for machine users
export function getMachinePromises(
  handlers: IHandlerMap['handlers'],
  formattedDate: string,
  customerId?: string
) {
  return [
    handlers.tagHandler?.getAll(),
    handlers.engineHoursHandler?.getAll(),
    handlers.fuelPriceHandler?.get(),
    handlers.fleetOverviewHandler?.getAll(),
    handlers.assetHandler?.getAllWithFuelAnalyticsData(),
    handlers.targetHandler?.getAll(customerId as string, fullYear),
    handlers.utilizationHandler?.get(
      'Today',
      formattedDate,
      formattedDate,
      true,
      true,
      'all',
      'daily',
      false,
      1,
      'engineHours'
    ),
    handlers.productionHandler?.get(
      'Today',
      formattedDate,
      formattedDate,
      true,
      true,
      'all',
      'daily'
    ),
    handlers.productionHandler?.getLoaderDetails(
      'Today',
      formattedDate,
      formattedDate,
      true,
      true,
      'loader',
      'daily'
    ),
  ].filter(Boolean);
}

// Internal user specific promises
function getInternalUserPromises(handlers: IHandlerMap['handlers']) {
  return [
    handlers.internalUserHandler?.getAll(),
    handlers.customerHandler?.getAll(),
  ].filter(Boolean);
}

// External user specific promises
function getExternalUserPromises(handlers: IHandlerMap['handlers']) {
  return [handlers.dtcHandler?.get()].filter(Boolean);
}

// Helper to resolve customer modules
async function resolveModules(handlers: IHandlerMap['handlers']) {
  // Get modules from handlers or Redux store
  let modules = handlers.modules;

  if (!modules) {
    const state = store.getState();
    modules = state.authReducer?.customer?.modules;
  }

  if (!modules && handlers.externalUserHandler) {
    try {
      await handlers.externalUserHandler.currentCustomer();
      const state = store.getState();
      modules = state.authReducer?.customer?.modules;
    } catch (error) {
      console.error('Error fetching current customer:', error);
    }
  }

  // Safe default if modules still not available (don't assume machines module)
  return (
    modules ?? {
      isPartsModule: false,
      isTiresModule: false,
      isMachinesModule: false,
    }
  );
}

// execute a fetch for the getAll method of each handler
export async function internalFetchAll({ handlers }: IHandlerMap) {
  await resolveModules(handlers);

  // Common promises - always called (includes assets which are needed for both TPMS and Machine users)
  const commonPromises = getCommonPromises(handlers, handlers.customerId);

  // Internal user specific promises - always called for internal users
  const internalUserPromises = getInternalUserPromises(handlers);

  // Combine all promises (removed tpms and machine specific promises)
  const allPromises = [...commonPromises, ...internalUserPromises];

  try {
    await Promise.allSettled(allPromises);
  } catch (error) {
    // do something with error
    console.log(error);
  }
}

export async function externalFetchAll({ handlers }: IHandlerMap) {
  await resolveModules(handlers);

  // Common promises - always called (includes assets which are needed for both TPMS and Machine users)
  const commonPromises = getCommonPromises(handlers, handlers.customerId);

  // External user specific promises - always called for external users
  const externalUserPromises = getExternalUserPromises(handlers);

  // Combine all promises (removed tpms and machine specific promises)
  const allPromises = [...commonPromises, ...externalUserPromises];

  try {
    await Promise.allSettled(allPromises);
  } catch (error) {
    // do something with error
    console.log(error);
  }
}
